/** Shown for anything that means "these credentials did not work". */
export const INVALID_CREDENTIALS_MESSAGE = 'Invalid credentials.';

/**
 * Maps a failed login into a message that is safe to show on the sign-in screen.
 *
 * Anything credential-shaped collapses to one generic line: telling the visitor
 * whether it was the username or the password that was wrong lets an attacker
 * enumerate accounts, and the API is deliberately generic for the same reason.
 * Raw API/axios text is never surfaced — a 500 would otherwise print an internal
 * error, and "Request failed with status code 401" is not an error message.
 *
 * Conditions that are *not* about the credentials keep their own message, since
 * saying "Invalid credentials." while the server is unreachable sends people off
 * resetting a password that was never wrong.
 *
 * @param {unknown} error rejected value from the login thunk, or an axios error
 * @returns {string} user-facing message
 */
export function loginErrorMessage(error) {
    if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
        return 'The server took too long to respond. Please try again.';
    }

    // An axios error with no `response` never reached the API: offline, DNS, CORS, refused.
    if (error && typeof error === 'object' && 'request' in error && !error.response) {
        return 'Cannot reach the server. Check your connection and try again.';
    }

    const status = Number(error?.response?.status ?? error?.status);

    if (status === 403) {
        return 'This account does not have access. Contact an administrator.';
    }
    if (status === 429) {
        return 'Too many sign-in attempts. Please wait a moment and try again.';
    }
    if (status >= 500) {
        return 'Something went wrong on our side. Please try again.';
    }

    // 400 / 401 / 404 / 422 and anything unrecognised: stay generic.
    return INVALID_CREDENTIALS_MESSAGE;
}

export default loginErrorMessage;
